import {leadSourceLabel,captureLabels,callOutcomeLabels,humanize} from './lead-database-model';
import {qualityLabels,type QualityGroup} from './lead-quality-model';

export const inquiryColumns=[
 ['first_at','Date/Time · ET',180],
 ['full_name','Customer Name',200],
 ['quality_group','Lead type',175],
 ['client_owner_name','Client Owner',200],
 ['account_manager','Account Manager',200],
 ['marketing_agency','Marketing Agency',175],
 ['map_city','Client Map City',190],
 ['map_zip','ZIP Code',125],
 ['source_website','Source Website',230],
 ['methods','Phone or Form Submit',180],
 ['call_outcomes','Answered?',180],
 ['total_call_seconds','Call Length',170],
 ['transcripts','Transcript',180],
 ['matrix_source','Lead source',190],
 ['source_origin','Source',175],
 ['form_info','Form Info',360],
 ['client_name','Client Name',210],
 ['routing_status','Coverage status',180],
] as const;
export type InquiryColumn=typeof inquiryColumns[number][0];
export const defaultInquiryColumns:InquiryColumn[]=inquiryColumns.filter(c=>!['client_name','routing_status'].includes(c[0])).map(c=>c[0]);
export type InquiryValues=Partial<Record<InquiryColumn,string|number|null>>&{forms?:number;calls?:number;call_duration_count?:number};
/** A column saved under its earlier name: "Paid v Organic" became the lead source row (6 Oct 2026). */
export const renameInquiryColumn=(key:unknown)=>key==='traffic_type'?'matrix_source':key;
export function upgradeInquiryColumnOrder(order:InquiryColumn[],version?:number):InquiryColumn[]{
 if(version===1)return order;
 const result=[...order];
 const added:InquiryColumn[]=['call_outcomes','total_call_seconds','transcripts'];
 result.splice(result.includes('methods')?result.indexOf('methods')+1:result.length,0,...added.filter(k=>!result.includes(k)));
 return result;
}
export function upgradeOwnerColumn(order:InquiryColumn[],version?:number):InquiryColumn[]{
 if(version===1||order.includes('client_owner_name'))return order;
 const result=[...order];
 const after=result.includes('client_name')?'client_name':'full_name';
 result.splice(result.includes(after)?result.indexOf(after)+1:0,0,'client_owner_name');
 return result;
}
export function upgradeManagerColumn(order:InquiryColumn[],version?:number):InquiryColumn[]{
 if(version===1||order.includes('account_manager'))return order;
 const result=[...order];
 const after=result.includes('client_owner_name')?'client_owner_name':'full_name';
 result.splice(result.includes(after)?result.indexOf(after)+1:0,0,'account_manager');
 return result;
}
export function callLength(seconds:unknown):string{
 if(seconds===null||seconds===undefined||seconds===''||!Number.isFinite(Number(seconds))||Number(seconds)<0)return 'Not recorded';
 const n=Math.floor(Number(seconds));return `${Math.floor(n/60)}:${String(n%60).padStart(2,'0')}`;
}
export function upgradeQualityColumn(order:InquiryColumn[],version?:number):InquiryColumn[]{
 if(version===1||order.includes('quality_group'))return order;
 const result=[...order];
 result.splice(result.includes('full_name')?result.indexOf('full_name')+1:0,0,'quality_group');
 return result;
}
export function inquiryCell(row:InquiryValues,key:InquiryColumn):string {
 const v=row[key];
 if(key==='quality_group')return qualityLabels[String(v) as QualityGroup]||'Needs review';
 if(key==='routing_status')return String(v||'—');
 if(['call_outcomes','total_call_seconds','transcripts'].includes(key)&&row.calls===0)return '—';
 if(key==='total_call_seconds'){
  const length=callLength(v);
  if(length==='Not recorded')return length;
  return row.calls&&row.calls>1?`${length} total · ${row.call_duration_count!=null&&row.call_duration_count<row.calls?`${row.call_duration_count}/${row.calls} lengths recorded`:`${row.calls} calls`}`:length;
 }
 if(key==='transcripts')return Number(v)>0?(row.calls&&row.calls>1?`${v}/${row.calls} received`:'Read transcript'):'Not received';
 if(v===null||v===undefined||v===''){
  if(key==='client_name'||key==='client_owner_name'||key==='account_manager')return '';
  if(key==='marketing_agency')return 'Not assigned';
  if(key==='source_website')return 'Website mapping required';
  if(['map_city','map_zip'].includes(key))return 'Not mapped';
  if(key==='form_info')return row.forms===0?'No form submitted':'Open form details';
  return 'Not recorded';
 }
 if(key==='first_at')return new Date(String(v)).toLocaleString('en-US',{timeZone:'America/New_York',dateStyle:'short',timeStyle:'short'});
 if(key==='matrix_source')return leadSourceLabel(String(v));
 if(key==='call_outcomes')return String(v).split(', ').map(x=>callOutcomeLabels[x]||humanize(x)).join(' · ');
 if(key==='methods'){
  const methods=String(v).split(', ');
  if(methods.length===2&&methods.includes('phone_call')&&methods.includes('form_submission'))return 'Phone + form';
  return methods.map(x=>captureLabels[x]||humanize(x)).join(' · ');
 }
 if(key==='source_origin'){
  const sources:Record<string,string>={direct:'Unattributed',unknown:'Unattributed',meta:'Meta',meta_ads:'Meta Ads',google_ads:'Google Ads',google_organic:'Google organic',google_business_profile:'Google Business Profile',google:'Google'};
  return sources[String(v)]||humanize(String(v));
 }
 return String(v);
}
